import { createFileRoute, Link, SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import {
    Card,
} from "@/components/ui/card"
import advancedFormat from 'dayjs/plugin/advancedFormat'
import dayjs from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    Api,
    is_shikhar_account
} from '@/lib/utils'
import { Issue } from '@/lib/interfaces'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Skeleton } from "@/components/ui/skeleton"
import EventTimeLine from "@/components/features/event-timeline"
import { useEffect, useState } from 'react'
import parse from 'html-react-parser'
import type { ReactNode } from 'react'
import { CloseIssueDialog } from '@/components/features/issues/close-issue'
import { toast } from 'sonner'
import { SearchIssueDialog } from '@/components/features/issues/search-issue'
import { CircleX, RefreshCw } from 'lucide-react'
import backIcon from '@/assets/paytm/back.png'

dayjs.extend(advancedFormat)

const DisplayOrderAttributes = ({
    fieldName,
    fieldValue,
    orderId
}: {
    fieldName: string
    fieldValue: string | undefined
    orderId?: boolean
}) => {
    return (
        <div>
            <p className='text-xs text-gray-500'>{fieldName}</p>
            {fieldValue !== undefined ? orderId ? (
                <Link to="/shikhar/orders/$orderId" params={{ orderId: fieldValue }}>
                    <p className='text-sm font-semibold underline text-blue-500 cursor-pointer'>{fieldValue || '--'}</p>
                </Link>
            ) : <p className='text-sm font-semibold'>{fieldValue || '--'}</p> : <Skeleton className="h-4 w-[250px]" />}
        </div>
    )
}

const getEventTimelines = (issue: Issue | undefined) => {
    let timelines: {
        name: string
        timestamp: string
        line3?: string
        line4?: string
        type?: 'red' | 'green'
    }[] = []
    if (issue) {
        if (issue.createdat) {
            timelines.push({
                name: 'Issue Raised',
                timestamp: issue.createdat
            })
        }

        if (issue.statusUpdatedat) {
            timelines.push({
                name: 'Status Updated',
                timestamp: issue.statusUpdatedat
            })
        }

        if (issue.relayedat) {
            timelines.push({
                name: 'Issue Relayed',
                timestamp: issue.relayedat
            })
        }

        if (issue.updatedat) {
            timelines.push({
                name: 'Issue Updated',
                timestamp: issue.updatedat
            })
        }

        if (issue.resolvedat) {
            timelines.push({
                name: 'Issue Resolved',
                timestamp: issue.resolvedat,
                type: 'green'
            })
        }

        if (issue.closedat) {
            timelines.push({
                name: 'Issue Closed',
                timestamp: issue.closedat,
            })
        }
    }

    return timelines
}

export const Route = createFileRoute('/shikhar/issues/$issueId')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { date: string } => {
        return {
            date: search.date as string || dayjs().format('YYYY-MM-DD')
        }
    }
})

const TopHeader = ({
    issueId,
    createdAt,
    status,
    onBackClick
}: {
    issueId: string,
    createdAt: string,
    status: string,
    onBackClick: () => void
}) => {
    return <div className='bg-transparent px-4 py-5 flex items-center justify-between'>
        <div className='flex items-start'>
            <img src={backIcon} className='w-6 mr-5 mt-1 cursor-pointer' onClick={onBackClick} />
            <div>
                <p className='text-lg font-bold'>{issueId || '-'}</p>
                <p className='text-sm font-medium text-gray-700'>{`Created at : ${createdAt || '-'}`}</p>
            </div>
        </div>
        <div className='px-6 py-2 rounded-3xl bg-black'>
            <p className='text-white font-semibold'>{status || '-'}</p>
        </div>
    </div>
}

const ActionButton = ({
    label,
    icon,
    disabled,
    variant = 'red',
    ...props
}: {
    label: string
    icon: ReactNode
    disabled?: boolean
    variant?: 'red' | 'blue'
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const colorClasses = variant === 'blue'
        ? 'border-blue-500 text-blue-500'
        : 'border-red-400 text-red-500'
    return (
        <button
            type="button"
            disabled={disabled}
            className={`flex items-center justify-center gap-3 border rounded-2xl py-3 px-5 active:opacity-70 ${colorClasses} ${disabled ? 'opacity-40' : 'cursor-pointer'}`}
            {...props}
        >
            {icon}
            <p className='font-semibold'>{label}</p>
        </button>
    )
}


function RouteComponent() {
    const { issueId } = Route.useParams()
    const queryClient = useQueryClient()
    const [showSearchIssue, setShowSearchIssue] = useState(false)
    const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
    const navigate = useNavigate({ from: Route.fullPath })
    const { date } = Route.useSearch()

    const { token, setPage, accountId } = useStore(useShallow(state => ({
        token: state.token,
        setPage: state.setPage,
        accountId: state.selectedAccount.id
    })))

    useEffect(() => {
        setPage('Issues')
    }, [])

    const getIssueInfo = useQuery({
        queryKey: ['issueInfo', issueId],
        queryFn: () => Api<{ status: number, issue: Issue }>('/webui/issue/info', "POST", { issue_id: issueId.replace(/\s+/g, '') }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false,
        retry: 1
    })

    useEffect(() => {
        if (getIssueInfo.error) {
            setErrMsg('Error fetching issue details.')
            setShowSearchIssue(true)
        }
    }, [getIssueInfo.error])

    const refreshIssue = useMutation({
        mutationFn: () => {
            return Api<{ status: number, issue: Issue }>('/webui/order/issue_status', "POST",
                { issue: { id: issueId } }, { token })
        },
        onError(error) {
            toast.error(error.message || "Error refreshing issue")
        },
        onSuccess(data, _v, _c) {
            queryClient.setQueryData(['issueInfo', issueId], (oldData: { status: number, issue: Issue }) => {
                return {
                    ...oldData,
                    issue: {
                        ...oldData.issue,
                        ...data.issue,
                    }
                }
            })
            toast.success('Issue refreshed')
        }
    })

    const issue = getIssueInfo.data?.issue
    const isShikhar = is_shikhar_account(accountId)

    return <div className='fixed top-0 bottom-0 left-0 right-0 bg-white z-50'>
        <TopHeader
            issueId={issue?.issueId || issueId}
            createdAt={issue?.createdat ? dayjs(issue.createdat).format('MMM D, hh:mm:ss A') : '-'}
            status={issue?.resolutionStatus || issue?.issueStatus || '-'}
            onBackClick={() => navigate({ to: '/shikhar/help', search: { date } })}
        />
        <div className='mt-4 h-[calc(100%-72px)] px-4 pb-4 *:my-4 overflow-auto'>
            <div className='border rounded-2xl overflow-hidden bg-white'>
                <div className='bg-gray-100 px-6 py-4 flex items-center gap-2'>
                    <p className='text-lg font-semibold'>Issue Details</p>
                </div>
                <div className='px-6 py-5'>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                        <DisplayOrderAttributes fieldName='Issue Id' fieldValue={issue?.issueId} />
                        {!isShikhar ? <DisplayOrderAttributes fieldName='Network Order Id' fieldValue={issue?.networkOrderId} /> : null}
                        <DisplayOrderAttributes fieldName='Order Id' fieldValue={issue?.orderId} orderId={true} />
                        <DisplayOrderAttributes fieldName='Client Order Id' fieldValue={issue?.clientOrderId} />
                        {!isShikhar ? <DisplayOrderAttributes fieldName='Status' fieldValue={issue?.issueStatus} /> : null}
                        <DisplayOrderAttributes fieldName='Resolution Status' fieldValue={issue?.resolutionStatus} />
                        {!isShikhar ? <DisplayOrderAttributes fieldName='Category' fieldValue={issue?.category} /> : null}
                        {!isShikhar ? <DisplayOrderAttributes fieldName='Sub-Category' fieldValue={issue?.subCategory} /> : null}
                        <DisplayOrderAttributes fieldName='Resolution Action' fieldValue={issue?.resolutionAction} />
                    </div>
                    <div className='mt-6 space-y-4'>
                        <div className='border rounded-xl px-4 py-3'>
                            <p className='text-xs text-gray-500'>Short Description</p>
                            <p className='text-sm font-semibold'>{issue?.shortDescription || '--'}</p>
                        </div>
                        <div className='border rounded-xl px-4 py-3'>
                            <p className='text-xs text-gray-500'>Long Description</p>
                            <p className='text-sm font-semibold'>{issue?.longDescription || '--'}</p>
                        </div>
                        <div className='border rounded-xl px-4 py-3'>
                            <p className='text-xs text-gray-500'>Resolution Description</p>
                            <div className='text-sm font-semibold overflow-auto'>
                                {/<\/?\w+>/.test((issue?.resolutionDescription || ''))
                                    ? parse((issue?.resolutionDescription || '').replace(/\u00a0/g, '<br/>'))
                                    : <p className='text-sm font-semibold'>{issue?.resolutionDescription || '--'}</p>}
                            </div>
                        </div>
                    </div>
                    {issue ? (
                        <div className='mt-6 flex flex-wrap items-center gap-4'>
                            <CloseIssueDialog issueId={issue.issueId} issueDescription={issue.longDescription} onSuccess={() => getIssueInfo.refetch()}
                                resolutionAction={issue.resolutionAction} resolutionDescription={issue.resolutionDescription}>
                                <ActionButton label="Close Issue" icon={<CircleX className='w-5 h-5 text-red-500' />} disabled={issue.issueStatus === 'CLOSED'} />
                            </CloseIssueDialog>
                            <ActionButton
                                label={refreshIssue.isPending ? 'Refreshing' : 'Refresh Issue'}
                                icon={<RefreshCw className='w-5 h-5 text-blue-500' />}
                                variant="blue"
                                onClick={() => refreshIssue.mutate()}
                                disabled={refreshIssue.isPending}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            {!isShikhar ? (
                <Card className='p-0'>
                    <div>
                        <div className='bg-gray-100 px-6 py-4 rounded-t-xl'>
                            <p className='font-semibold text-lg'>Timeline</p>
                        </div>
                        <div className='px-7'>
                            <EventTimeLine events={getEventTimelines(issue)} />
                        </div>
                    </div>
                </Card>
            ) : null}
            {!isShikhar ? (
                <Card className='p-0'>
                    <div>
                        <div className='bg-gray-100 px-6 py-4 rounded-t-xl'>
                            <p className='font-semibold text-lg'>Payment Details</p>
                        </div>
                        <div className='py-6 lg:px-40 md:px-10 px-5'>
                            <table className='w-full border-collapse [&_tr]:last:[&_td]:border-none  [&_td]:border-b [&_td]:p-2 [&_td]:last:text-right [&_td]:last:font-medium text-sm'>
                                <tr>
                                    <td className='font-medium'>Description</td>
                                    <td className='font-medium'>Amount</td>
                                </tr>
                                <tr>
                                    <td>Refund amount</td>
                                    <td>{`? ${issue?.refundAmount || 0}`}</td>
                                </tr>
                                <tr>
                                    <td>Expected refund amount</td>
                                    <td>{`? ${issue?.expectedRefundAmount || 0}`}</td>
                                </tr>
                                <tr>
                                    <td>Issued refund amount</td>
                                    <td>{`? ${issue?.issuedRefundAmount || 0}`}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </Card>
            ) : null}
            <SearchIssueDialog open={showSearchIssue} onClose={(resetSearch) => {
                setShowSearchIssue(false)
                if (resetSearch) {
                    navigate({
                        search: () => ({
                            search: false,
                            date
                        })
                    })
                }
            }} errMsg={errMsg} clearErrMsg={() => setErrMsg(undefined)} searchedIssueId={issueId} />
        </div>
    </div>
}
